import { concatStyleSets, IDropdownStyles } from '@fluentui/react';

import { IH2OTheme } from '../../models/components.models';
import { FontSizes, Sizes, Transitions, ZIndices } from '../../themes/styles';
import { CommonStyles } from '../../themes/themes';

const optionIconStyle = {
  '& .dropdown-option': {
    paddingLeft: Sizes.padding + 8,
  },
  '& .dropdown-option__icon': {
    position: 'absolute',
    left: Sizes.padding,
  },
};

const dropdownTitlePaddingRight = 35;

export const dropdownStylesDefault = (theme: IH2OTheme): Partial<IDropdownStyles> => ({
  root: {
    '&.multi-select': {
      '.ms-Dropdown-title': {
        paddingLeft: 8,
      },
    },
  },
  label: {
    color: theme.semanticColors?.inputReadOnlyText,
    fontSize: FontSizes.input,
    fontWeight: 'normal',
  },
  title: {
    height: 'auto',
    minHeight: Sizes.height,
    border: `1px solid ${theme.semanticColors?.inputBorder}`,
    borderRadius: Sizes.borderRadius,
    lineHeight: Sizes.height - 2, // border * 2
    paddingLeft: Sizes.padding - 1, // border * 1
    color: theme.semanticColors?.inputReadOnlyText,
    '&:focus': {
      borderColor: theme.semanticColors?.inputActiveBorder,
    },
    paddingRight: dropdownTitlePaddingRight,
    transition: `all ${Transitions.short}`,
    '&:hover': {
      borderColor: theme.semanticColors?.inputActiveBorder,
    },
    '&:active, &:focus': {
      borderColor: theme.semanticColors?.inputActiveBorder,
    },
  },
  caretDownWrapper: {
    height: Sizes.height - 2,
    lineHeight: Sizes.height - 2,
    right: Sizes.padding - 1,
  },
  caretDown: {
    color: theme.semanticColors?.inputReadOnlyText,
  },
  dropdown: {
    // when :focus, it adds ::after...
    '::after': {
      display: 'none',
    },
    '&:hover .ms-Dropdown-title': {
      color: theme.semanticColors?.textQuaternary,
      borderColor: theme.semanticColors?.inputActiveBorder,
    },
    '&:active .ms-Dropdown-title': {
      borderColor: theme.semanticColors?.inputActiveBorder,
    },
    '&.is-disabled': {
      backgroundColor: theme.semanticColors?.inputDisabledBackground,
      pointerEvents: 'none',
      '.ms-Dropdown-title': {
        color: theme.semanticColors?.textDisabled,
        backgroundColor: theme.semanticColors?.inputDisabledBackground,
      },
      '.ms-Dropdown-caretDownWrapper > i': {
        color: theme.semanticColors?.textDisabled,
      },
    },
  },
  dropdownItem: {
    padding: `0 ${Sizes.padding}px`,
    color: theme.semanticColors?.textQuaternary,
    height: Sizes.height,
    lineHeight: Sizes.height,
    minHeight: Sizes.height,
    '&:hover': {
      color: theme.semanticColors?.inputReadOnlyText,
      backgroundColor: `${theme.semanticColors?.inputListItemHoveredBackground} !important`,
    },
  },
  dropdownItemSelected: {
    height: Sizes.height,
    lineHeight: Sizes.height,
    minHeight: Sizes.height,
    padding: `0 ${Sizes.padding}px`,
    backgroundColor: `${theme.semanticColors?.inputListItemHoveredBackground} !important`,
    '&:hover': {
      color: theme.semanticColors?.inputReadOnlyText,
    },
  },
  /* Height must be redundantly assigned to disabled items. Does not inherit height from normal dropdown item styles */
  /* (◡_◡) */
  /* Same for the padding: Fluent renders a disabled option with `dropdownItemDisabled` *instead of*
     `dropdownItem`, so without this the row keeps Fluent's 8px and sits left of its enabled siblings. */
  dropdownItemDisabled: {
    height: Sizes.height,
    lineHeight: Sizes.height,
    minHeight: Sizes.height,
    padding: `0 ${Sizes.padding}px`,
  },
  dropdownItemSelectedAndDisabled: {
    height: Sizes.height,
    lineHeight: Sizes.height,
    minHeight: Sizes.height,
    padding: `0 ${Sizes.padding}px`,
  },
  dropdownOptionText: {
    margin: 0,
  },
});

export const dropdownStylesIcon = (theme: IH2OTheme): Partial<IDropdownStyles> => ({
  root: {
    position: 'relative',
    '& .dropdown-icon': {
      position: 'absolute',
      zIndex: ZIndices.first,
      left: Sizes.padding,
      lineHeight: Sizes.height,
      color: theme.semanticColors?.inputReadOnlyText,
    },
  },
  title: {
    paddingLeft: Sizes.padding * 2 - 1 + 8,
  },
});

export const dropdownStylesOptionIcon: Partial<IDropdownStyles> = {
  title: optionIconStyle,
  dropdownItem: optionIconStyle,
  dropdownItemSelected: optionIconStyle,
};

export const dropdownStylesInlineLeft = (theme: IH2OTheme): Partial<IDropdownStyles> => ({
  title: {
    borderColor: theme.semanticColors?.dropdownInlineBorder,
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
    '&:focus': {
      borderColor: theme.semanticColors?.dropdownInlineBorder,
    },
    paddingRight: dropdownTitlePaddingRight,
  },
});

export const dropdownStylesEditor = (theme: IH2OTheme): Partial<IDropdownStyles> =>
  concatStyleSets(dropdownStylesDefault(theme), {
    root: {
      height: '100%',
    },
    dropdown: {
      height: '100%',
      border: `1px solid ${theme.semanticColors?.inputBorder}`,
      backgroundColor: theme.palette?.white,
      borderRadius: Sizes.borderRadius,
      '&:focus': {
        borderColor: theme.semanticColors?.inputActiveBorder,
      },
      '&:hover': {
        borderColor: theme.semanticColors?.inputActiveBorder,
      },
      '&:active, &:focus': {
        borderColor: theme.semanticColors?.inputActiveBorder,
      },
      ...CommonStyles.flexCenterVertically,
    },
    title: {
      border: 0,
    },
    caretDownWrapper: {
      height: '100%',
      lineHeight: 'auto',
      ...CommonStyles.flexCenterVertically,
      right: Sizes.padding - 1,
    },
  }) as Partial<IDropdownStyles>;

export const dropdownStylesChat = (): Partial<IDropdownStyles> => ({
  title: { lineHeight: '38px', height: '40px' },
  caretDownWrapper: { lineHeight: '38px', height: '40px' },
});
